import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { impersonationAPI } from '../services/api';
import './ImpersonationModal.css';

interface User {
  id: number;
  name: string;
  email: string;
  is_admin: boolean;
  created_at: string;
  auth_provider: string;
  email_verified: boolean;
}

interface ImpersonationModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ImpersonationModal: React.FC<ImpersonationModalProps> = ({ isOpen, onClose }) => {
  const { startImpersonation } = useAuth();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [isImpersonating, setIsImpersonating] = useState(false);

  useEffect(() => {
    if (isOpen) {
      loadUsers();
    }
  }, [isOpen, page, search]);

  const loadUsers = async () => {
    try {
      setLoading(true);
      setError('');
      
      const response = await impersonationAPI.getUsers({
        page,
        limit: 10,
        search: search.trim()
      });
      
      const { users: fetchedUsers, pagination } = response.data.data;
      setUsers(fetchedUsers);
      setTotalPages(pagination.totalPages);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error cargando usuarios');
    } finally {
      setLoading(false);
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    setPage(1); // Reset to first page when searching
  };

  const handleImpersonate = async (userId: number, userName: string) => {
    if (!window.confirm(`¿Estás seguro de que quieres impersonar a ${userName}?`)) {
      return;
    }

    try {
      setIsImpersonating(true);
      await startImpersonation(userId.toString());
      onClose();
    } catch (error: any) {
      setError(error.message);
    } finally {
      setIsImpersonating(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const getProviderIcon = (provider: string) => {
    switch (provider) {
      case 'google':
        return '🔵';
      case 'local':
        return '📧';
      default:
        return '👤';
    }
  };

  if (!isOpen) return null;

  return (
    <div className="impersonation-modal-overlay" onClick={onClose}>
      <div className="impersonation-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>👤 Impersonar Usuario</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        <div className="modal-body">
          {/* Search */}
          <div className="search-section">
            <input
              type="text"
              placeholder="Buscar por nombre o email..."
              value={search}
              onChange={handleSearchChange}
              className="search-input"
            />
          </div>

          {error && (
            <div className="error-message">
              ⚠️ {error}
            </div>
          )}

          {/* Users List */}
          <div className="users-list">
            {loading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Cargando usuarios...</p>
              </div>
            ) : users.length === 0 ? (
              <div className="empty-state">
                <p>No se encontraron usuarios</p>
              </div>
            ) : (
              users.map((user) => (
                <div key={user.id} className="user-item">
                  <div className="user-info">
                    <div className="user-main">
                      <span className="provider-icon">{getProviderIcon(user.auth_provider)}</span>
                      <div className="user-details">
                        <h4>{user.name}</h4>
                        <p>{user.email}</p>
                      </div>
                    </div>
                    <div className="user-meta">
                      <span className={`status ${user.email_verified ? 'verified' : 'unverified'}`}>
                        {user.email_verified ? '✅ Verificado' : '⏳ Sin verificar'}
                      </span>
                      <span className="join-date">
                        Unido: {formatDate(user.created_at)}
                      </span>
                    </div>
                  </div>
                  <button
                    className="impersonate-btn"
                    onClick={() => handleImpersonate(user.id, user.name)}
                    disabled={isImpersonating}
                  >
                    {isImpersonating ? '...' : '👤 Impersonar'}
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 1 || loading}
                className="page-btn"
              >
                ← Anterior
              </button>
              <span className="page-info">
                Página {page} de {totalPages}
              </span>
              <button
                onClick={() => setPage(page + 1)}
                disabled={page === totalPages || loading}
                className="page-btn"
              >
                Siguiente →
              </button>
            </div>
          )}
        </div>

        <div className="modal-footer">
          <p className="warning-text">
            ⚠️ La impersonificación se registra en los logs de auditoría
          </p>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationModal;